import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Button, Box } from '@mui/material';
import ReturnToWorkLetterDialog from './ReturnToWorkLetterDialog';
import SickLetterDialog from './SickLetterDialog';
import MaternityLetterDialog from './MaternityLetterDialog';

const Absences = ({ employee }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rtwId, setRtwId] = useState(null);
  const [sickId, setSickId] = useState(null);
  const [mat, setMat] = useState({ requests: [], letters: [] });
  const [matSel, setMatSel] = useState(null);

  useEffect(() => {
    if (employee && employee.id) {
      fetchAbsences(employee.id);
    }
    if (employee && employee.id) {
      api.get(`/letter-gen/maternity/employee/${employee.id}`)
        .then((res) => setMat({ requests: res.data.requests || [], letters: res.data.letters || [] }))
        .catch(() => setMat({ requests: [], letters: [] }));
    }
  }, [employee]);

  const fetchAbsences = async (employeeId) => {
    try {
      setLoading(true);
      const response = await api.get(`/absences/employee/${employeeId}`);
      if (response.data && Array.isArray(response.data)) {
        const newestFirst = [...response.data].sort((x, y) => {
          const d = String(y.date).localeCompare(String(x.date));
          return d !== 0 ? d : (y.id || 0) - (x.id || 0);
        });
        setAbsences(newestFirst);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected data format received');
      }
    } catch (err) {
      console.error('Error fetching absences:', err);
      setError('Failed to fetch absences: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Typography>Loading absences...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const shortDate = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString() : 'not set');

  return (
    <>
    {mat.requests.length > 0 && (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Maternity leave letters</Typography>
        {mat.requests.map((r) => (
          <Box key={r.id} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Leave from {shortDate(r.start_date)} - due date {shortDate(r.expected_date)} ({r.status})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {mat.letters.map((l) => (
                <Button key={l.key} size="small" variant="outlined"
                  onClick={() => setMatSel({ id: r.id, key: l.key, label: l.label })}>{l.label}</Button>
              ))}
            </Box>
          </Box>
        ))}
      </Paper>
    )}
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Days Absent</TableCell>
            <TableCell>Return to Work Date</TableCell>
            <TableCell>Fit Note</TableCell>
            <TableCell>Self Certification</TableCell>
            <TableCell>Interview Completed</TableCell>
            <TableCell>Letters</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {absences.map((absence) => (
            <TableRow key={absence.id}>
              <TableCell>{new Date(absence.date).toLocaleDateString()}</TableCell>
              <TableCell>{absence.reason}</TableCell>
              <TableCell>{absence.number_of_days_absent}</TableCell>
              <TableCell>{absence.return_to_work_date ? new Date(absence.return_to_work_date).toLocaleDateString() : 'N/A'}</TableCell>
              <TableCell>{absence.fit_note_received ? 'Yes' : 'No'}</TableCell>
              <TableCell>{absence.self_certification_form ? 'Yes' : 'No'}</TableCell>
              <TableCell>{absence.back_to_work_interview_completed ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" onClick={() => setSickId(absence.id)}>Sickness letter</Button>
                  <Button size="small" variant="outlined" onClick={() => setRtwId(absence.id)}>Return-to-work letter</Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ReturnToWorkLetterDialog open={!!rtwId} absenceId={rtwId} onClose={() => setRtwId(null)} />
      <SickLetterDialog open={!!sickId} absenceId={sickId} onClose={() => setSickId(null)} />
    </TableContainer>
    <MaternityLetterDialog
      open={!!matSel} requestId={matSel && matSel.id} letterKey={matSel && matSel.key}
      letterLabel={matSel && matSel.label} onClose={() => setMatSel(null)} />
    </>
  );
};

export default Absences;